import { isValidYoutubeUrl } from './youtubeUrl'

export interface LiveTvChannel {
  id: string
  title: string
  youtubeUrl: string
  enabled: boolean
  category?: 'football' | 'lottery' | 'other'
  sortOrder?: number
}

export interface HuayYoutubeLiveConfig {
  youtubeUrl: string
  enabled: boolean
  label?: string
  updatedAt?: string
}

export type HuayYoutubeLiveMap = Record<string, HuayYoutubeLiveConfig>

export function parseLiveTvChannels(raw: unknown): LiveTvChannel[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((item) => item && typeof item === 'object')
    .map((item) => item as LiveTvChannel)
    .filter((item) => item.id && item.title)
}

export function parseHuayYoutubeLive(raw: unknown): HuayYoutubeLiveMap {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: HuayYoutubeLiveMap = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!key || !value || typeof value !== 'object') continue
    const v = value as Partial<HuayYoutubeLiveConfig>
    const youtubeUrl = typeof v.youtubeUrl === 'string' ? v.youtubeUrl.trim() : ''
    if (!youtubeUrl) continue
    out[String(key).trim()] = {
      youtubeUrl,
      enabled: v.enabled !== false,
      label: typeof v.label === 'string' ? v.label : undefined,
      updatedAt: typeof v.updatedAt === 'string' ? v.updatedAt : undefined,
    }
  }
  return out
}

/**
 * Look up a YouTube live config for a lottery round.
 * Tries `huayCode` (uppercased), `huayId`, and `_id` in that order — matching
 * how tenant-admin stores entries (see `frontend/tenant-admin/src/utils/liveStreamHuayUtils.ts`).
 */
export function lookupHuayYoutubeLiveForRound(
  round: { huayId?: string; huayCode?: string; _id?: string } | null | undefined,
  map: HuayYoutubeLiveMap | null | undefined
): HuayYoutubeLiveConfig | null {
  if (!round || !map) return null
  const candidates: string[] = []
  if (round.huayCode) candidates.push(String(round.huayCode).trim().toUpperCase())
  if (round.huayId) candidates.push(String(round.huayId).trim())
  if (round._id) candidates.push(String(round._id).trim())
  for (const key of candidates) {
    if (!key) continue
    const entry = map[key]
    if (entry && entry.enabled !== false && isValidYoutubeUrl(entry.youtubeUrl)) {
      return entry
    }
  }
  return null
}

/**
 * Convert `huayYoutubeLive` map into virtual `LiveTvChannel` entries so they
 * can be displayed alongside football channels on the `/live-tv` page.
 *
 * @param map           Global huay youtube live map.
 * @param huayNameByKey Optional Thai-name lookup keyed by huayCode/huayId/_id
 *                       (uppercase). Used as a fallback when admin hasn't saved
 *                       a Thai `label` yet.
 */
export function huayYoutubeLiveToChannels(
  map: HuayYoutubeLiveMap | null | undefined,
  huayNameByKey?: Record<string, string> | null
): LiveTvChannel[] {
  if (!map) return []
  return Object.entries(map)
    .filter(([, entry]) => entry && entry.enabled !== false && isValidYoutubeUrl(entry.youtubeUrl))
    .map(([key, entry], index) => ({
      id: `huay:${key}`,
      title: resolveHuayLiveTitle(key, entry, huayNameByKey),
      youtubeUrl: entry.youtubeUrl,
      enabled: true,
      category: 'lottery' as const,
      sortOrder: 1000 + index,
    }))
}

function resolveHuayLiveTitle(
  key: string,
  entry: HuayYoutubeLiveConfig,
  huayNameByKey?: Record<string, string> | null
): string {
  const label = entry.label?.trim()
  if (label) return label
  if (huayNameByKey) {
    const normalizedKey = String(key || '').trim().toUpperCase()
    const fromMap = huayNameByKey[normalizedKey] || huayNameByKey[String(key || '').trim()]
    if (fromMap && fromMap.trim()) return fromMap.trim()
  }
  return formatHuayKeyAsTitle(key)
}

function formatHuayKeyAsTitle(key: string): string {
  return String(key || '')
    .replace(/^HUAY[_\s]*/i, '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase()) || key
}

export function getActiveLiveTvChannels(channels: LiveTvChannel[]): LiveTvChannel[] {
  return channels
    .filter((channel) => channel.enabled !== false && isValidYoutubeUrl(channel.youtubeUrl))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
}

/**
 * True when the user-side `/live-tv` shortcut should be visible.
 * Either the per-tenant Live TV add-on is on with a valid football channel,
 * OR there is at least one enabled lottery YouTube live entry.
 */
export function hasActiveLiveTv(
  channels: LiveTvChannel[],
  liveTvEnabled?: boolean,
  huayYoutubeLive?: HuayYoutubeLiveMap | null
): boolean {
  if (huayYoutubeLive && huayYoutubeLiveToChannels(huayYoutubeLive).length > 0) return true
  if (liveTvEnabled !== true) return false
  return getActiveLiveTvChannels(channels).length > 0
}

export function countActiveLiveStreams(
  channels: LiveTvChannel[],
  liveTvEnabled?: boolean,
  huayYoutubeLive?: HuayYoutubeLiveMap | null
): number {
  const lottery = huayYoutubeLiveToChannels(huayYoutubeLive || null).length
  const football = liveTvEnabled === true ? getActiveLiveTvChannels(channels).length : 0
  return lottery + football
}
